import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  InlineNotification,
  TableCell,
  Button,
  Modal,
  InlineLoading,
} from '@carbon/react';
import { Connect, SubtractAlt } from '@carbon/react/icons';
import { FormRow } from 'components/forms';
import { SkeletonTableRows } from 'components/skeleton-table-rows';
import type {
  CRMAccountResult,
  CRMAccountResponse,
  EntityResponse,
  EntityLinkResponse,
} from 'types/api-responses';
import type { ApiPayloads } from 'types/api-payloads';
import type { CreateResourceAtom } from 'atoms/create-resource-atom';
import type { ErrorsType } from 'lib/types';
import { formatMutationResource } from 'utils/misc';
import { useReducer, useState } from 'react';
import { useSetAtom } from 'jotai';
import { deleteEntityLinkMutationAtom } from './atoms';
import { EMPTY_CELL } from 'utils/empty-cell';
import { useDeleteEntityLink } from './hooks';
import { NoResults } from './no-results';
import style from './crm-accounts.module.scss';

interface CRMAccountsTableProps {
  crmAccountsData: CreateResourceAtom<CRMAccountResult, ErrorsType>;
  onLinkAccount?: () => void;
  entity?: EntityResponse;
  onDeleteSuccess?: () => void;
}

/**
 * The CRM accounts linked to an entity, each with a remove action, or an empty
 * state offering to link one.
 * @param {CRMAccountsTableProps} props - The accounts resource, the entity and the handlers
 * @return {JSX.Element} The table, its loading or error state, or the empty state
 */
export function CRMAccountsTable({
  crmAccountsData,
  onLinkAccount,
  entity,
  onDeleteSuccess,
}: CRMAccountsTableProps) {
  const { loading, error, data } = formatMutationResource(crmAccountsData);

  if (loading) {
    return (
      <FormRow>
        <TableContainer>
          <Table aria-label="CRM Accounts">
            <TableHead>
              <TableRow>
                <TableHeader>Account Name</TableHeader>
                <TableHeader>Type</TableHeader>
                <TableHeader>Billing Address</TableHeader>
                <TableHeader>Phone</TableHeader>
                <TableHeader>Action</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              <SkeletonTableRows rows={5} columns={5} />
            </TableBody>
          </Table>
        </TableContainer>
      </FormRow>
    );
  }

  if (error) {
    return (
      <InlineNotification
        kind="error"
        title={
          error.type === 'error'
            ? Array.isArray(error.message)
              ? error.message.join(', ')
              : 'Failed to load CRM accounts'
            : 'Failed to load CRM accounts'
        }
        lowContrast
      />
    );
  }

  if (
    (!data?.results ||
      !Array.isArray(data.results) ||
      data.results.length === 0) &&
    (!data || !error)
  ) {
    return (
      <div>
        <NoResults
          headerText="No CRM Accounts Linked"
          helperText="Search Salesforce for an existing account to link, or create a new CRM account from this entity's details."
        />
        {onLinkAccount && (
          <div className={style.linkButtonContainer}>
            <Button
              size="sm"
              renderIcon={Connect}
              iconDescription="Link CRM Account"
              onClick={onLinkAccount}
            >
              Link CRM Account
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <FormRow>
      <TableContainer className={style.tableContainer}>
        <Table aria-label="CRM Accounts">
          <TableHead>
            <TableRow>
              <TableHeader>Account Name</TableHeader>
              <TableHeader>Type</TableHeader>
              <TableHeader>Billing Address</TableHeader>
              <TableHeader>Phone</TableHeader>
              <TableHeader>Action</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.results.map((account: CRMAccountResponse) => {
              const entityLink = entity
                ? account.entities?.find((ent) => ent.id === entity.id)
                : null;
              const entityLinkId = entityLink?.entityLinkId;

              return (
                <TableRow key={account.salesforceId}>
                  <TableCell>
                    <a
                      href={account.salesforceHref}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {account.name}
                    </a>
                  </TableCell>
                  <TableCell>{account.type || EMPTY_CELL}</TableCell>
                  <TableCell>{account.billingAddress || EMPTY_CELL}</TableCell>
                  <TableCell>{account.phone || EMPTY_CELL}</TableCell>
                  <TableCell>
                    {entityLinkId && entity && (
                      <DeleteEntityLinkButton
                        entityId={entity.id}
                        entityLinkId={entityLinkId}
                        onDeleteSuccess={onDeleteSuccess}
                      />
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </FormRow>
  );
}

interface DeleteEntityLinkButtonProps {
  entityId: number;
  entityLinkId: string;
  onDeleteSuccess?: () => void;
}

/**
 * Removes an entity's link to a CRM account after a confirm, following the
 * removal's workflow to completion.
 * @param {DeleteEntityLinkButtonProps} props - The entity, its link and the success handler
 * @return {JSX.Element} The remove button and its confirm modal
 */
function DeleteEntityLinkButton({
  entityId,
  entityLinkId,
  onDeleteSuccess,
}: DeleteEntityLinkButtonProps) {
  const [openModal, toggleOpenModal] = useReducer((s) => !s, false);
  const [deleteState, setDeleteState] = useState({
    isDeleting: false,
    deleteChannel: null as string | null,
    deleteEvents: [] as { name: string; label?: string }[],
    deleteComplete: false,
    deleteError: null as string | null,
  });
  const deleteEntityLinkMutation = useSetAtom(deleteEntityLinkMutationAtom);

  // Subscribe to Pusher channel for DELETE completion
  useDeleteEntityLink({
    deleteState,
    setDeleteState,
    onDeleteSuccess,
    toggleOpenModal,
  });

  /**
   * Starts the removal; its workflow channel then reports completion.
   * @param {string} linkId - The entity link to remove
   */
  const deleteEntityLink = (linkId: string) => {
    setDeleteState((prev) => ({
      ...prev,
      isDeleting: true,
      deleteError: null,
    }));

    deleteEntityLinkMutation({
      url: `/entities/${entityId}/entity_links/${linkId}`,
      type: 'DELETE',
      // A DELETE sends no body; the payload type is the create call's.
      data: {} as ApiPayloads['entityLinks']['payload'],
      onSuccess: (data: EntityLinkResponse) => {
        const channel = data.resources?.notifications?.channel;
        const events = data.resources?.notifications?.events || [];
        if (channel) {
          setDeleteState((prev) => ({
            ...prev,
            deleteChannel: channel,
            deleteEvents: events,
          }));
        } else {
          setDeleteState((prev) => ({
            ...prev,
            isDeleting: false,
            deleteError: 'Failed to get workflow channel. Please try again.',
          }));
        }
      },
      onError: (error: unknown) => {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to delete entity link. Please try again.';
        setDeleteState((prev) => ({
          ...prev,
          isDeleting: false,
          deleteChannel: null,
          deleteComplete: false,
          deleteError: errorMessage,
        }));
      },
    });
  };

  /** Clears a finished or failed removal so the modal opens fresh. */
  const resetDeleteState = () => {
    setDeleteState({
      isDeleting: false,
      deleteChannel: null,
      deleteEvents: [],
      deleteComplete: false,
      deleteError: null,
    });
  };

  /** Confirms the removal of this link. */
  const handleDelete = () => {
    deleteEntityLink(entityLinkId);
  };

  return (
    <>
      <Button
        kind="danger--ghost"
        renderIcon={SubtractAlt}
        iconDescription="Remove"
        tooltipPosition="left"
        hasIconOnly
        onClick={() => toggleOpenModal()}
      />
      <Modal
        open={openModal}
        modalHeading="Are you sure you want to delete this?"
        danger
        primaryButtonText={
          deleteState.isDeleting ? 'Deleting...' : 'Yes, delete'
        }
        primaryButtonDisabled={deleteState.isDeleting}
        secondaryButtonText="No, cancel"
        onRequestSubmit={handleDelete}
        onRequestClose={() => {
          if (!deleteState.isDeleting) {
            toggleOpenModal();
            resetDeleteState();
          }
        }}
        className={style.deleteModal}
      >
        {deleteState.isDeleting && (
          <div>
            <InlineLoading description="Deleting entity link..." />
          </div>
        )}
        {deleteState.deleteError && (
          <InlineNotification
            kind="error"
            title={deleteState.deleteError}
            lowContrast
            hideCloseButton
            style={{ marginBottom: '1rem' }}
          />
        )}
        <div className={style.deleteModalText}>
          It will be removed immediately. You can&apos;t undo this action.
        </div>
      </Modal>
    </>
  );
}
